import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { AGENTS_PATH } from '../../config';
import { tokens } from '../../theme';
import type { AgentVersionListProps } from './types';

/**
 * Every released version of an Agent Skill, newest first, as links — the way a package
 * page lists its versions. The newest is marked **Latest**, and the one on screen
 * is marked as the current page.
 *
 * **The version is in the query string, not the path**, so `/toolkit/agents/x` is
 * always the newest and a link to a particular one is `?version=1.0.0`. A reader
 * pasting an address gets what the address says, and the bare address never goes
 * stale.
 *
 * The first entry is the newest because the service orders them; this does not
 * sort. Sorting here would be a second ordering rule beside the one that knows
 * `1.10.0` follows `1.9.0`.
 */
const RELEASED = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** An unparseable date is left out rather than drawn as "Invalid Date". */
const releasedOn = (releasedAt: string | undefined): string | undefined => {
  if (releasedAt === undefined) {
    return undefined;
  }

  const date = new Date(releasedAt);

  return Number.isNaN(date.getTime()) ? undefined : RELEASED.format(date);
};

export const AgentVersionList = ({ agentName, versions, selected }: AgentVersionListProps) => (
  <Box
    component="ul"
    sx={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 1 }}
  >
    {versions.map((stub, index) => {
      const { version } = stub;
      const isSelected = version === selected;
      const date = releasedOn(stub.releasedAt);

      return (
        <Box component="li" key={version}>
          <RouterLink
            to={`${AGENTS_PATH}/${encodeURIComponent(agentName)}?version=${encodeURIComponent(version)}`}
            aria-current={isSelected ? 'page' : undefined}
            style={{ textDecoration: 'none' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                paddingInline: 3,
                paddingBlock: 2,
                borderRadius: `${String(tokens.radius.md)}px`,
                backgroundColor: isSelected ? 'surface.sunken' : 'transparent',
                '&:hover': { backgroundColor: 'surface.sunken' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    fontFamily: tokens.fontFamily.mono,
                    color: isSelected ? 'text.primary' : 'text.link',
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {version}
                </Typography>

                <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
                  {index === 0 && (
                    <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>
                      Latest
                    </Typography>
                  )}
                  {date !== undefined && (
                    <Typography component="span" sx={{ fontSize: 11, color: 'text.meta' }}>
                      {date}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* The tag's message, **as text**: it is whatever the maintainer typed, so it
                  is never parsed. One line, because the list is a list of versions. */}
              {stub.releaseNotes !== undefined && (
                <Typography
                  component="span"
                  noWrap
                  sx={{ fontSize: 12, color: 'text.secondary' }}
                  title={stub.releaseNotes}
                >
                  {stub.releaseNotes.split('\n')[0]}
                </Typography>
              )}
            </Box>
          </RouterLink>
        </Box>
      );
    })}
  </Box>
);
